"use client";

import { CONTACT_US_API } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { Loader2Icon } from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";
import { PressReleaseMarketing } from "../news/components/press-release-marketing";
import { usePathname } from "next/navigation";

const ContactUs = ({
	showPressReleaseMarketing = true,
}: {
	showPressReleaseMarketing?: boolean;
}) => {
	const [form, setForm] = useState({ name: "", email: "", message: "" });
	const [errorMessage, setErrorMessage] = useState<string>("");
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
	const pathname = usePathname();
	const isCommunityEducationPortalPage = pathname === "/services/community-education-portal";

	const onSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		try {
			setIsLoading(true);
			const url = new URL(CONTACT_US_API);
			const res = await fetch(url, {
				method: "POST",
				body: JSON.stringify(form),
			});

			const body = await res.json();
			const message = body?.message;

			if (res.ok) {
				toast.success(message);
				setIsSubmitted(true);
				if (pathname === "/services/community-education-portal") {
					window.location.href = "https://cal.com/akshaya-sujaatha-msuzeb/proptechbuzz-exploratory-call-30-min-meeting";
				}
			} else {
				toast.error(message);
			}

			setErrorMessage("");
		} catch (error) {
			console.log(error);
		} finally {
			setIsLoading(false);
		}
	};

	const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		setForm({
			...form,
			[e.target.name]: e.target.value,
		});
	};

	return (
		<>
			<section className="mx-auto w-11/12 max-w-2xl py-12 md:w-full">
				{isSubmitted ? (
					<div className="text-center">
						<h2 className="text-3xl font-bold">Thank You!</h2>
						<p className="mt-4 text-lg text-gray-600">
							We{"'"}ve received your message, we will get back to you soon!
						</p>
					</div>
				) : (
					<>
						{!isCommunityEducationPortalPage && (
							<>
								<h2 className="mb-3 text-3xl font-bold">Contact Us</h2>
								<p className="mb-12 max-w-md text-lg text-gray-600">
									Please send us your message by submitting the form below, our team will get back
									to you
								</p>
							</>
						)}

						<form onSubmit={onSubmit}>
							<div className="flex flex-col gap-2">
								<label className="mb-1 text-gray-600">Name</label>
								<input
									type="text"
									name="name"
									onChange={onChange}
									placeholder="Your name"
									className="w-full rounded-lg border border-slate-300 p-4 focus:outline-gray-200"
									required
								/>

								<label className="mb-1 mt-6 text-gray-600">Email</label>
								<input
									type="email"
									name="email"
									onChange={onChange}
									placeholder="Your email"
									className="w-full rounded-lg border border-slate-300 p-4 focus:outline-gray-200"
									required
								/>

								<label className="mb-1 mt-6 text-gray-600">Message</label>
								<textarea
									placeholder="Type your message"
									name="message"
									onChange={onChange}
									className="w-full rounded-lg border border-slate-300 p-4 focus:outline-gray-200"
									required
								/>

								<button className="mt-6 rounded-lg bg-brand-primary px-8 py-4 text-center text-lg font-semibold text-white">
									{isLoading ? (
										<Loader2Icon className="mx-auto h-8 w-8 animate-spin stroke-white" />
									) : isCommunityEducationPortalPage ? (
										"Launch Your Community Portal"
									) : (
										"Send"
									)}
								</button>

								{isCommunityEducationPortalPage && (
									<p className="mt-6 text-center text-[13px] text-[#394B59]">
										By submitting, you agree to our Terms of Service and Privacy Policy.
									</p>
								)}
							</div>
						</form>

						<p
							className={cn(
								"text-sm text-red-400 transition-opacity duration-150 ease-linear",
								errorMessage ? "opacity-100" : "opacity-0"
							)}
						>
							{errorMessage}
						</p>
					</>
				)}
			</section>
			{showPressReleaseMarketing && <PressReleaseMarketing />}
		</>
	);
};

export default ContactUs;
